import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Factory } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supplyService } from '@/services/supplyService';
import { invalidateAllSupplyTableCaches } from '@/utils/supplyTableCache';

export function SupplyProductionScopeSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;

  const settings = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
  });

  const patch = useMutation({
    mutationFn: (payload: {
      production_scope_enabled?: boolean;
      on_demand_stock_not_committed?: boolean;
    }) => supplyService.updateOrganizationSettings(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
      invalidateAllSupplyTableCaches();
    },
  });

  const scopeEnabled = settings.data?.production_scope_enabled ?? false;
  const stockNotCommitted = settings.data?.on_demand_stock_not_committed ?? false;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Factory className="h-5 w-5 text-stocker-primary" />
          {t('settings.supplyProductionScope.title')}
        </CardTitle>
        <CardDescription>
          {t('settings.supplyProductionScope.description')}{' '}
          {t('settings.supplyProductionScope.plantOnlyHint')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('settings.saveError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={scopeEnabled}
                disabled={!canEdit || patch.isPending}
                onChange={(event) =>
                  patch.mutate({ production_scope_enabled: event.target.checked })
                }
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyProductionScope.scopeLabel')}
                </span>
                <span className="block text-xs text-gray-500">
                  {scopeEnabled
                    ? t('settings.supplyProductionScope.scopeOnHint')
                    : t('settings.supplyProductionScope.scopeOffHint')}
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={stockNotCommitted}
                disabled={!canEdit || patch.isPending}
                onChange={(event) =>
                  patch.mutate({ on_demand_stock_not_committed: event.target.checked })
                }
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyProductionScope.stockLabel')}
                </span>
                <span className="block text-xs text-gray-500">
                  {stockNotCommitted
                    ? t('settings.supplyProductionScope.stockOnHint')
                    : t('settings.supplyProductionScope.stockOffHint')}
                </span>
              </span>
            </label>
          </>
        )}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.supplyProductionScope.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
